import api from "../services/authService";

/**
 * Chat-answer feedback client (companion to dms_backend PR #1975).
 *
 * Every assistant chat answer carries a small thumbs-up / thumbs-down
 * affordance; ratings and comments land in `ChatFeedback` on the backend,
 * where the weekly improvement review consumes them ("the fix queue").
 *
 * Gate: the backend reuses the `ai.brief.feedback` capability — one AI
 * feedback loop, one gate. Same 403 contract as `listBriefFeedback` in
 * `analysis.js`: a 403 means "hide the feedback UI", never an error.
 *
 * This lives in its own module (not `matter.js`) on purpose: existing
 * ChatPane tests mock `service/matter` with an explicit factory, and
 * adding functions there would break every one of those mocks.
 */

// Session-scoped probe cache. The capability cannot change mid-session,
// so one GET decides visibility for every ChatPane mount until reload.
let accessPromise = null;

/**
 * GET /api/agent/chat/feedback/?status=new — capability probe.
 *
 * Resolves `true` when the current user may leave chat feedback,
 * `false` on 403 or any transport failure (fail closed: the buttons
 * are an enhancement, never worth an error surface).
 */
export const probeChatFeedbackAccess = () => {
  if (!accessPromise) {
    accessPromise = api
      .get(`/agent/chat/feedback/?status=new`, {
        validateStatus: (status) => status === 200 || status === 403,
      })
      .then((response) => response?.status === 200)
      .catch(() => false);
  }
  return accessPromise;
};

/**
 * Mark feedback as forbidden for the rest of the session. Called when a
 * POST comes back 403 (capability revoked mid-session) so later mounts
 * don't re-show the buttons.
 */
export const revokeChatFeedbackAccess = () => {
  accessPromise = Promise.resolve(false);
};

/** Test-only: clear the session cache between cases. */
export const resetChatFeedbackAccessCache = () => {
  accessPromise = null;
};

/**
 * POST /api/agent/chat/feedback/
 *
 * Payload: `{ message_id, rating, comment }` where `message_id` is the
 * backend `MatterChatMessage` id (exposed by thread hydration). The
 * backend 400s a down/neutral rating without a comment and 404s a
 * message that isn't the caller's.
 */
export const createChatFeedback = ({ messageId, rating, comment }) => {
  if (messageId == null) {
    return Promise.reject(
      new Error("createChatFeedback: messageId is required")
    );
  }
  return api.post(`/agent/chat/feedback/`, {
    message_id: messageId,
    rating,
    comment: comment || "",
  });
};
